import React, { useState, useEffect } from 'react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    service: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    // Scroll to contact form if hash is present
    if (window.location.hash === '#contact-form') {
      const element = document.getElementById('contact-form');
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create email body
      const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Company: ${formData.company || 'Not provided'}
Phone: ${formData.phone || 'Not provided'}
Service Interest: ${formData.service || 'Not specified'}

Message:
${formData.message}
      `;

      // Send email using the configured mail server
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          service: formData.service
        }),
      });

      if (response.ok) {
        const result = await response.json();
        alert('Thank you for your message! We will get back to you soon.');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
          service: ''
        });
      } else {
        const errorResult = await response.json();
        throw new Error(errorResult.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      alert('There was an error sending your message. Please try again or contact us directly at contact@sanerconsulting.co.uk');
    }
  };

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="section">
        <div className="container">
          <h1 className="hero-title">Contact Us</h1>
          <p className="subtitle">
            Ready to start your transformation journey? Connect with our experts to discuss your specific needs and objectives.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section id="contact-form" className="section section-dark">
        <div className="container">
          <div className="grid grid-2">
            <div className="contact-info">
              <h2 className="section-title">Get in Touch</h2>
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="contact-icon">📧</div>
                  <div>
                    <h3>Email</h3>
                    <p>contact@sanerconsulting.co.uk</p>
                    <p className="contact-note">For prompt replies, please use our contact form or email</p>
                  </div>
                </div>
                <div className="contact-method">
                  <div className="contact-icon">📞</div>
                  <div>
                    <h3>Phone</h3>
                    <p>+44 20 3883 7573</p>
                  </div>
                </div>
                <div className="contact-method">
                  <div className="contact-icon">📍</div>
                  <div>
                    <h3>Registered Office</h3>
                    <p>SG House, 6 St Cross Road<br />Winchester, Hampshire<br />SO23 9HX, England</p>
                    <p className="contact-note">Please do not write to our registered address for inquiries</p>
                  </div>
                </div>
                <div className="contact-method">
                  <div className="contact-icon">💬</div>
                  <div>
                    <h3>Live Chat</h3>
                    <p>Available on our website</p>
                    <p className="contact-note">Recommended for fastest response</p>
                  </div>
                </div>
              </div>

              <div className="legal-info">
                <h3>Company Information</h3>
                <ul>
                  <li><strong>Company:</strong> Saner Consulting Limited</li>
                  <li><strong>Registration:</strong> England Number 10471658</li>
                  <li><strong>VAT Number:</strong> 255130531</li>
                  <li><strong>Terms of Service:</strong> Available on our website</li>
                </ul>
              </div>

              <div className="business-hours">
                <h3>Business Hours</h3>
                <ul>
                  <li>Monday - Friday: 9:00 AM - 6:00 PM</li>
                  <li>Saturday: 10:00 AM - 2:00 PM</li>
                  <li>Sunday: Closed</li>
                </ul>
              </div>
            </div>

            <div className="contact-form-container">
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="service">Service Interest</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select a service</option>
                    <option value="transformation-capabilities">Transformation Capabilities</option>
                    <option value="transformation-assurance">Transformation Assurance</option>
                    <option value="portfolio-programme-delivery">Portfolio & Programme Delivery</option>
                    <option value="helping-businesses">Helping Large & Small Businesses</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-input"
                    placeholder="Tell us about your project and how we can help..."
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">How long does a typical transformation project take?</h3>
              <p className="faq-answer">
                Project timelines vary based on scope and complexity, typically ranging from 3-18 months. 
                We provide detailed timelines during our initial consultation.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Do you work with small and medium businesses?</h3>
              <p className="faq-answer">
                Yes, we work with organizations of all sizes. Our solutions are scalable and can be 
                tailored to meet the specific needs and budgets of SMBs.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">What industries do you specialize in?</h3>
              <p className="faq-answer">
                We have extensive experience across multiple industries including financial services, 
                healthcare, manufacturing, retail, technology, and public sector.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">How do you measure project success?</h3>
              <p className="faq-answer">
                We establish clear KPIs and success metrics at project outset, focusing on measurable 
                business outcomes such as ROI, efficiency gains, and user adoption rates.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;